/**
 * The teacher's way in (spec 8.1, 10.4): email and password, and the one-time setup link the
 * operator sends by hand. There is no mail provider in the pilot, so "Passwort vergessen"
 * says who to write to — `admin reset-teacher` prints a fresh link.
 */

import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { publicJson } from "../shared/api.js";
import { getSession, startSession } from "../shared/auth-store.js";
import { ApiClientError, germanMessage } from "../shared/errors.js";
import { Shell } from "./TeacherLayout.js";
import { ErrorLine, Field, TButton, inputClass } from "./ui.js";

interface TeacherAuthResponse {
  accessToken: string;
  refreshToken: string;
  teacher: { id: string; email: string };
}

/** Spec 8.1: the operator's contact address, since there is no automated reset in the pilot. */
const OPERATOR_EMAIL = "veselin.lakic@ajooda.ch";

/** Build spec 3.2: at least 10 characters, nothing else. */
const MIN_PASSWORD_LENGTH = 10;

function AuthCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Shell>
      <div className="flex min-h-screen items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-6">
          <p className="m-0 mb-1 text-[15px] font-black text-[var(--color-ink)]">Scrolivo</p>
          <h1 className="m-0 mb-5 text-[24px] font-bold">{title}</h1>
          {children}
        </div>
      </div>
    </Shell>
  );
}

export function TeacherLoginScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [showForgotten, setShowForgotten] = useState(false);

  if (getSession()?.subjectType === "teacher") return <Navigate to="/lehrer" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const response = await publicJson<TeacherAuthResponse>("/auth/teacher/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      await startSession("teacher", response.accessToken, response.refreshToken);
      void navigate("/lehrer", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiClientError && err.code === "invalid_credentials"
          ? "E-Mail oder Passwort stimmt nicht."
          : germanMessage(err),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthCard title="Anmelden">
      <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
        <Field label="E-Mail">
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className={inputClass}
            autoComplete="username"
            required
          />
        </Field>
        <Field label="Passwort">
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className={inputClass}
            autoComplete="current-password"
            required
          />
        </Field>
        {error && <ErrorLine>{error}</ErrorLine>}
        <TButton type="submit" variant="primary" disabled={pending}>
          {pending ? "…" : "Anmelden"}
        </TButton>
      </form>

      <div className="mt-5 border-t border-[var(--color-line)] pt-4">
        <TButton variant="quiet" onClick={() => setShowForgotten((open) => !open)}>
          Passwort vergessen?
        </TButton>
        {showForgotten && (
          <p className="mt-2 mb-0 text-[14px] leading-relaxed text-[var(--color-muted)]">
            Schreib an Scrolivo ({OPERATOR_EMAIL}), du bekommst einen neuen Einrichtungslink.
          </p>
        )}
      </div>
    </AuthCard>
  );
}

/** `/lehrer/setup/:token` — the link `admin create-teacher` prints, valid 7 days (spec 8.1). */
export function TeacherSetupScreen() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError("Das Passwort braucht mindestens " + MIN_PASSWORD_LENGTH + " Zeichen.");
      return;
    }
    if (password !== repeat) {
      setError("Die beiden Passwörter sind nicht gleich.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const response = await publicJson<TeacherAuthResponse>("/auth/teacher/setup", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      await startSession("teacher", response.accessToken, response.refreshToken);
      void navigate("/lehrer", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiClientError && err.code === "unauthorized"
          ? "Dieser Link ist abgelaufen. Bitte melde dich bei Scrolivo."
          : germanMessage(err),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthCard title="Passwort setzen">
      <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
        <Field label="Neues Passwort" hint={"Mindestens " + MIN_PASSWORD_LENGTH + " Zeichen."}>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className={inputClass}
            autoComplete="new-password"
            required
          />
        </Field>
        <Field label="Passwort wiederholen">
          <input
            type="password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            className={inputClass}
            autoComplete="new-password"
            required
          />
        </Field>
        {error && <ErrorLine>{error}</ErrorLine>}
        <TButton type="submit" variant="primary" disabled={pending}>
          {pending ? "…" : "Passwort setzen"}
        </TButton>
      </form>
    </AuthCard>
  );
}
